import Phaser from "phaser";

const BOOT_SOUND = "/assets/sounds/boot.ogg";
const LOOP_SOUND = "/assets/sounds/loop.ogg";
const CLICK_SOUND = "/assets/sounds/click.wav";
const KEY_SOUND = "/assets/sounds/key.ogg";
const ERROR_SOUND = "/assets/sounds/error.ogg";
const READ_SOUND = "/assets/sounds/read.ogg";
const FALLBACK_BOOT_MS = 8000;
const SFX_VOLUME_MIN = 0.85;
const SFX_VOLUME_MAX = 1;

function randomSfxVolume(): number {
    return SFX_VOLUME_MIN + Math.random() * (SFX_VOLUME_MAX - SFX_VOLUME_MIN);
}

export function preloadTerminalAudio(loader: Phaser.Loader.LoaderPlugin): void {
    loader.audio("boot", BOOT_SOUND);
    loader.audio("loop", LOOP_SOUND);
    loader.audio("click", CLICK_SOUND);
    loader.audio("key", KEY_SOUND);
    loader.audio("error", ERROR_SOUND);
    loader.audio("read", READ_SOUND);
}

export interface BootAudioCallbacks {
    onBootStart?: () => void;
    onBootEnd?: () => void;
    onLoopStart: () => void;
}

export class TerminalAudio {
    private bootSound: Phaser.Sound.BaseSound | null = null;
    private loopSound: Phaser.Sound.BaseSound | null = null;
    private readSound: Phaser.Sound.BaseSound | null = null;
    private bootStarted = false;
    private loopStarted = false;
    private loopCallbacks: BootAudioCallbacks | null = null;
    private pictureReadCount = 0;

    constructor(private readonly scene: Phaser.Scene) {}

    bindUnlock(retryBootAudio: () => void): void {
        const unlock = (): void => {
            if (this.scene.sound.locked) {
                this.scene.sound.unlock();
            }
            retryBootAudio();
        };

        window.addEventListener("keydown", unlock);
        this.scene.game.canvas.addEventListener("pointerdown", unlock);
        this.scene.sound.once(Phaser.Sound.Events.UNLOCKED, retryBootAudio);
    }

    getBootDurationMs(): number {
        if (!this.scene.cache.audio.exists("boot")) {
            return FALLBACK_BOOT_MS;
        }

        const probe = this.scene.sound.add("boot");
        const ms = probe.duration * 1000;
        probe.destroy();
        return ms > 0 ? ms : FALLBACK_BOOT_MS;
    }

    tryPlayBoot(callbacks: BootAudioCallbacks): void {
        this.loopCallbacks = callbacks;
        this.attemptPlayBoot();
    }

    private attemptPlayBoot(): void {
        if (this.bootStarted || !this.scene.cache.audio.exists("boot")) {
            return;
        }

        if (this.scene.sound.locked) {
            return;
        }

        const callbacks = this.loopCallbacks;
        if (!callbacks) return;

        this.bootStarted = true;
        this.bootSound = this.scene.sound.add("boot");
        callbacks.onBootStart?.();
        this.bootSound.once(Phaser.Sound.Events.COMPLETE, () => {
            callbacks.onBootEnd?.();
            this.startLoop();
        });
        this.bootSound.play();
    }

    ensureLoop(): void {
        this.startLoop();
    }

    stopAll(): void {
        this.bootSound?.stop();
        this.bootSound?.destroy();
        this.bootSound = null;
        this.loopSound?.stop();
        this.loopSound?.destroy();
        this.loopSound = null;
        this.resetPictureRead();
    }

    beginPictureRead(): void {
        if (!this.scene.cache.audio.exists("read") || this.scene.sound.locked) {
            return;
        }

        this.pictureReadCount += 1;
        if (this.pictureReadCount !== 1) {
            return;
        }

        this.readSound = this.scene.sound.add("read", { loop: true });
        this.readSound.play();
    }

    endPictureRead(): void {
        if (this.pictureReadCount <= 0) {
            return;
        }

        this.pictureReadCount -= 1;
        if (this.pictureReadCount === 0) {
            this.stopPictureRead();
        }
    }

    resetPictureRead(): void {
        this.pictureReadCount = 0;
        this.stopPictureRead();
    }

    private stopPictureRead(): void {
        this.readSound?.stop();
        this.readSound?.destroy();
        this.readSound = null;
    }

    reset(): void {
        this.stopAll();
        this.bootStarted = false;
        this.loopStarted = false;
        this.loopCallbacks = null;
    }

    playClick(): void {
        this.playSfx("click", randomSfxVolume());
    }

    playKey(): void {
        this.playSfx("key", randomSfxVolume());
    }

    playError(): void {
        this.playSfx("error");
    }

    playSound(key: string, volume = 1): void {
        this.playSfx(key, volume);
    }

    private playSfx(key: string, volume = 1): void {
        if (!this.scene.cache.audio.exists(key) || this.scene.sound.locked) {
            return;
        }

        const sound = this.scene.sound.add(key, { volume });
        sound.once(Phaser.Sound.Events.COMPLETE, () => sound.destroy());
        sound.play();
    }

    private startLoop(): void {
        if (this.loopStarted || !this.scene.cache.audio.exists("loop")) {
            return;
        }

        this.loopStarted = true;
        this.loopSound = this.scene.sound.add("loop", { loop: true });
        this.loopSound.play();
        this.loopCallbacks?.onLoopStart();
        this.loopCallbacks = null;
    }
}
